import { useState, useEffect } from "react";
import api, { MAD, apiError } from "../lib/api";
import { useData } from "../hooks/useData";
import { usePos } from "../context/PosContext";
import { PageHeader, ExportButtons, Loader, Empty, StatCard } from "../components/ui-kit";
import { Modal, Field } from "../components/Modal";
import { exportExcel, exportPDF } from "../lib/exporters";
import { Plus, Trash2, Wallet } from "lucide-react";
import { toast } from "sonner";

const CATS = ["Matières premières", "Emballages", "Salaires", "Loyer", "Électricité", "Marketing", "Transport", "Remise B2B", "Équipement", "Nettoyage", "Divers"];
const EMPTY = { libelle: "", categorie: "Matières premières", montant: "", fournisseur: "", commentaire: "", date: "", pos_id: "", mode_paiement: "especes", est_charge_fixe: false, charge_id: "", periode_concernee: "" };
const MODES = { especes: "Espèces", carte: "Carte", virement: "Virement", retenue_glovo: "Retenue Glovo (ni caisse ni banque)" };

export default function Expenses() {
  const { posList, isAdmin, writePosId } = usePos();
  const { data, loading, refetch } = useData("/expenses");
  const { data: charges } = useData("/charges-fixes");
  const chargeMap = Object.fromEntries(charges.map((c) => [c.id, c.nom]));
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  // Départements affectables à une dépense : boutiques + Distribution B2B (jamais le laboratoire géré à part… mais on l'autorise pour les charges labo)
  const deptOptions = posList.filter((p) => true);
  const posMap = Object.fromEntries(posList.map((p) => [p.id, p.nom]));

  useEffect(() => {
    if (open && isAdmin && !form.pos_id && deptOptions.length) setForm((f) => ({ ...f, pos_id: deptOptions[0].id }));
  }, [open, deptOptions.length]); // eslint-disable-line

  const openNew = () => { setForm({ ...EMPTY, date: new Date().toISOString().slice(0, 10), pos_id: writePosId || deptOptions[0]?.id || "" }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    const dest = isAdmin ? form.pos_id : writePosId;
    if (!dest) return toast.error("Sélectionnez un département");
    if (form.est_charge_fixe && !form.charge_id) return toast.error("Choisissez la charge fixe concernée");
    try {
      await api.post(`/expenses?pos_id=${dest}`, { ...form, montant: parseFloat(form.montant), charge_id: form.est_charge_fixe ? form.charge_id : "" });
      toast.success("Dépense ajoutée"); setOpen(false); refetch();
    } catch (e2) { toast.error(apiError(e2.response?.data?.detail)); }
  };

  const remove = async (x) => {
    if (!window.confirm("Supprimer cette dépense ?")) return;
    await api.delete(`/expenses/${x.id}`); toast.success("Supprimée"); refetch();
  };

  const fmt = (s) => new Date(s).toLocaleDateString("fr-FR");
  const total = data.reduce((a, x) => a + x.montant, 0);

  const cols = [
    { header: "Date", value: (r) => fmt(r.date) },
    { header: "Département", value: (r) => posMap[r.pos_id] || "Commun" },
    { header: "Paiement", value: (r) => MODES[r.mode_paiement] || "Espèces" },
    { header: "Charge fixe", value: (r) => chargeMap[r.charge_id] || "" },
    { header: "Libellé", value: (r) => r.libelle },
    { header: "Catégorie", value: (r) => r.categorie },
    { header: "Fournisseur", value: (r) => r.fournisseur || "" },
    { header: "Nature / Commentaire", value: (r) => r.commentaire || "" },
    { header: "Montant (DH)", value: (r) => r.montant },
  ];

  return (
    <div>
      <PageHeader title="Dépenses" subtitle="Suivi des charges et coûts de la boutique.">
        <ExportButtons onExcel={() => exportExcel("depenses_tiramessi", cols, data)} onPDF={() => exportPDF("Dépenses TIRAMESSI", cols, data)} />
        <button className="tm-btn" onClick={openNew} data-testid="add-expense-btn"><Plus size={16} /> Nouvelle dépense</button>
      </PageHeader>

      <div className="mb-6 max-w-xs">
        <StatCard label="Total dépenses" value={MAD(total)} icon={Wallet} accent="#F44336" testid="expenses-total" />
      </div>

      {loading ? <Loader /> : data.length === 0 ? <Empty text="Aucune dépense." /> : (
        <div className="tm-card overflow-x-auto" data-testid="expenses-table">
          <table className="tm-table">
            <thead>
              <tr><th>Date</th><th>Département</th><th>Libellé</th><th>Paiement</th><th>Catégorie</th><th>Fournisseur</th><th>Nature / Commentaire</th><th className="text-right">Montant</th><th></th></tr>
            </thead>
            <tbody>
              {data.map((x) => (
                <tr key={x.id} data-testid={`expense-row-${x.id}`}>
                  <td className="text-[var(--tm-muted)] whitespace-nowrap">{fmt(x.date)}</td>
                  <td className="text-[var(--tm-muted)]">{posMap[x.pos_id] || "Commun"}</td>
                  <td className="font-semibold">{x.libelle}{x.charge_id && <span className="block text-[11px] font-normal" style={{ color: "var(--tm-gold)" }} data-testid={`expense-charge-${x.id}`}>Charge fixe : {chargeMap[x.charge_id] || "liée"}</span>}</td>
                  <td className="text-[var(--tm-muted)]">{MODES[x.mode_paiement] || "Espèces"}</td>
                  <td><span className="tm-badge" style={{ background: "var(--tm-surface-2)", color: "var(--tm-muted)" }}>{x.categorie}</span></td>
                  <td className="text-[var(--tm-muted)]">{x.fournisseur}</td>
                  <td className="text-[var(--tm-muted)] max-w-[260px]">{x.commentaire}</td>
                  <td className="text-right tabular" style={{ color: "#F44336" }}>{MAD(x.montant)}</td>
                  <td className="text-right"><button onClick={() => remove(x)} className="text-[var(--tm-muted)] hover:text-[#F44336]"><Trash2 size={16} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="Nouvelle dépense" testid="expense-modal">
        <form onSubmit={save}>
          <Field label="Libellé"><input className="tm-input" required value={form.libelle} onChange={(e) => setForm({ ...form, libelle: e.target.value })} data-testid="expense-libelle" /></Field>
          {isAdmin && (
            <Field label="Département">
              <select className="tm-input" value={form.pos_id} onChange={(e) => setForm({ ...form, pos_id: e.target.value })} data-testid="expense-departement">
                {deptOptions.map((p) => <option key={p.id} value={p.id}>{p.nom}</option>)}
                <option value="commun">Commun (réparti au prorata du CA)</option>
              </select>
            </Field>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Mode de paiement">
              <select className="tm-input" value={form.mode_paiement} onChange={(e) => setForm({ ...form, mode_paiement: e.target.value })} data-testid="expense-mode-paiement">
                {Object.entries(MODES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </Field>
            <label className="flex items-center gap-2 mt-6 text-sm" data-testid="expense-charge-fixe-label">
              <input type="checkbox" checked={form.est_charge_fixe} onChange={(e) => setForm({ ...form, est_charge_fixe: e.target.checked })} data-testid="expense-charge-fixe" />
              Paiement d'une charge fixe
            </label>
          </div>
          {form.categorie === "Salaires" && (
            <Field label="Période concernée (mois du salaire, facultatif)">
              <input type="month" className="tm-input" value={form.periode_concernee} onChange={(e) => setForm({ ...form, periode_concernee: e.target.value })} data-testid="expense-periode-concernee" />
            </Field>
          )}
          {form.est_charge_fixe && (
            <Field label="Charge fixe concernée (sort des coûts variables)">
              <select className="tm-input" value={form.charge_id} onChange={(e) => setForm({ ...form, charge_id: e.target.value })} data-testid="expense-charge-select">
                <option value="">— Choisir —</option>
                {charges.filter((c) => c.actif !== false).map((c) => <option key={c.id} value={c.id}>{c.nom} ({MAD(c.montant)})</option>)}
              </select>
            </Field>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Catégorie">
              <select className="tm-input" value={form.categorie} onChange={(e) => setForm({ ...form, categorie: e.target.value })}>
                {CATS.map((c) => <option key={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Montant (DH)"><input type="number" step="0.01" className="tm-input" required value={form.montant} onChange={(e) => setForm({ ...form, montant: e.target.value })} data-testid="expense-montant" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Fournisseur"><input className="tm-input" value={form.fournisseur} onChange={(e) => setForm({ ...form, fournisseur: e.target.value })} /></Field>
            <Field label="Date"><input type="date" className="tm-input" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></Field>
          </div>
          <Field label="Nature de la dépense (commentaire)">
            <textarea className="tm-input" rows={3} placeholder="Expliquez la nature de cette dépense…" value={form.commentaire} onChange={(e) => setForm({ ...form, commentaire: e.target.value })} data-testid="expense-commentaire" />
          </Field>
          <button type="submit" className="tm-btn w-full mt-2" data-testid="expense-save">Enregistrer</button>
        </form>
      </Modal>
    </div>
  );
}
